import type { MailboxItem } from "../hooks/useMailboxItem";
import { PaperclipIcon } from "../../../shared/components/icons";
import styles from "./EmailContextBar.module.css";

/** A new draft has no subject until the user types one — label it rather than
 *  rendering a blank banner. */
const EMPTY_DRAFT_SUBJECT = "New message";

function getInitials(email: string): string {
  const name = email.split("@")[0] ?? "";
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/** "first@x.com", or "first@x.com +2" for a longer list. Empty when there are none. */
function summariseRecipients(to: readonly string[]): string {
  if (to.length === 0) return "";
  const extra = to.length > 1 ? ` +${to.length - 1}` : "";
  return `${to[0] ?? ""}${extra}`;
}

interface EmailContextBarProps {
  readonly mailboxItem: MailboxItem;
}

/**
 * The open-item banner. Sits between the tab bar and the thread and names the
 * mail item AiVA holds in context, so the user can confirm what "reply to
 * this" will act on before sending a prompt.
 *
 * Two variants:
 *   - Read mode — sender's initials, subject, and from → to.
 *   - Compose mode — no sender (the user is the sender), so the draft is
 *     labelled and its subject shown instead.
 *
 * Display-only; nothing here is interactive.
 */
export function EmailContextBar({ mailboxItem }: Readonly<EmailContextBarProps>) {
  const isCompose = mailboxItem.isCompose ?? false;

  // Nothing in context — e.g. the pane was opened from the ribbon with no
  // message selected. The banner is conditional, so render nothing at all
  // rather than an empty strip above the thread.
  if (!isCompose && !mailboxItem.subject && !mailboxItem.from) return null;

  const recipients = summariseRecipients(mailboxItem.to);
  let meta = "";
  if (isCompose) {
    meta = recipients ? `To: ${recipients}` : "";
  } else {
    meta = recipients ? `${mailboxItem.from} → ${recipients}` : mailboxItem.from;
  }

  return (
    <div className={styles.emailBar}>
      {!isCompose && (
        <div className={styles.emailAvatar}>
          {getInitials(mailboxItem.from)}
        </div>
      )}
      <div className={styles.emailInfo}>
        <div className={styles.emailSubject}>
          {isCompose && <span className={styles.emailLabel}>Draft:</span>}
          {isCompose ? mailboxItem.subject || EMPTY_DRAFT_SUBJECT : mailboxItem.subject}
        </div>
        {meta && <div className={styles.emailMeta}>{meta}</div>}
        {mailboxItem.attachments.length > 0 && (
          <span className={styles.emailAttachment}>
            <PaperclipIcon />
            {mailboxItem.attachments.length === 1
              ? mailboxItem.attachments[0]?.name
              : `${mailboxItem.attachments.length} attachments`}
          </span>
        )}
      </div>
    </div>
  );
}
